import React, { useRef, useCallback, memo, useState, useEffect, useMemo } from 'react';
import { View, Pressable, Platform, useWindowDimensions } from 'react-native';
import * as Animatable from 'react-native-animatable';
import styles from '@stylesheet';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import apis from '@api/api';
import useGlobalUser from '@hooks/getGlobalUser';
import parseByDelimiter from '@utils/parseByDelimiter';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { selectFavouriteData, selectUserId } from '@reduxLocal/selectors/userSelectors';
import { useQuery } from '@tanstack/react-query';
import { DefaultRootState } from '@reduxLocal/persistState';
import { HANDLE_FAVOURITE, SET_ACTIVE_ITEM } from '@reduxLocal/actions/types';
import getDaysAgo from '@utils/getDaysAgo';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { SizableText, XStack, YStack, Card, H4, Text, Popover, Separator } from 'tamagui';
import Button from "@components/UI/Button/Button";
import { FontAwesome5, FontAwesome, Feather, MaterialIcons, Ionicons } from '@expo/vector-icons';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import { t } from 'i18next';
import moment from 'moment';


// Modern icon mapping for data types
const iconMap = [
  { type: 'misc', icon: <MaterialIcons name="storage" size={20} color="#ffffff" /> },
  { type: 'customer', icon: <Ionicons name="people" size={20} color="#ffffff" /> },
  { type: 'product', icon: <MaterialIcons name="inventory" size={20} color="#ffffff" /> },
  { type: 'inventory', icon: <MaterialIcons name="inventory-2" size={20} color="#ffffff" /> }
];

// Component interfaces
interface DataItemProps {
  item: any;
  index?: number;
  handleShareState?: (item: any) => void;
  handleGenLink?: (item: any) => void;
  handleQuickAdd?: (item: any) => void;
  handleSearch?: (item: any) => void;
  compactMode?: boolean;
  listHorizontal?: boolean;
  handleRefresh?: () => void;
  handleShareModal?: (item: any) => void;
  handleActionModal?: (item: any) => void;
  handleDeleteModal?: (item: any) => void;
  handleSettingsModal?: (item: any) => void;
  availableWidth?: number;
}

const DataItem: React.FC<DataItemProps> = memo(({
  item,
  index,
  handleShareState,
  handleGenLink,
  handleQuickAdd,
  handleSearch,
  listHorizontal,
  handleRefresh,
  compactMode,
  handleShareModal,
  handleActionModal,
  handleDeleteModal,
  handleSettingsModal,
  availableWidth
}) => {
  // Hooks and state
  const userId = useSelector(selectUserId);
  const favouriteDataIds = useSelector(selectFavouriteData, shallowEqual) || [];
  const isFavourite = useMemo(() => favouriteDataIds.includes(item._id), [favouriteDataIds, item._id]);
  const { showConfirm } = useAlertDialog();
  const navigation = useNavigation<any>();
  const globalUser = useGlobalUser();
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { width } = useWindowDimensions();
  const isTablet = width > 768;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Track view on component mount
  useEffect(() => {
    const trackView = async () => {
      try {
        await apis.trackDataView(item._id);
      } catch (error) {
        console.log('Failed to track view:', error);
      }
    };
    
    if (item._id) {
      trackView();
    }
  }, [item._id]);

  // Track interaction function
  const trackInteraction = useCallback(async (action: string) => {
    try {
      await apis.trackDataInteraction(item._id, action);
    } catch (error) {
      console.log('Failed to track interaction:', error);
    }
  }, [item._id]);

  // Responsive variables
  const isSmallScreen = width < 600;
  const isMediumScreen = width >= 600 && width < 1024;
  const showCompactMode = isSmallScreen || (availableWidth && availableWidth < 350);

  // Query for folder stats
  const { data: folderStats, refetch } = useQuery({
    queryKey: ['folderStats', item._id],
    queryFn: () => apis.getFolderStats(item._id),
    staleTime: 60000, // 1 minute
    enabled: !!item._id,
  });

  // Utility functions
  const isOwner = useCallback(() => {
    return item.owner?._id === userId || item.createdBy === userId;
  }, [item.owner?._id, item.createdBy, userId]);

  const hasPermission = useCallback(() => {
    return item.permission || isOwner();
  }, [item.permission, isOwner]);

  // Event handlers
  const handleEditTitle = useCallback(() => {
    trackInteraction('edit_title');
    showConfirm('Edit Data Title', 'Enter new title for the data', {
      confirmText: 'Save',
      cancelText: "Cancel",
      textField: true,
      initialValue: item.dataTitle,
    }).then(async (res: string) => {
      await apis.changeDataTitle(item._id, res);
      handleRefresh?.();
    });
  }, [item._id, item.dataTitle, handleRefresh, showConfirm, trackInteraction]);

  const handleFavourite = useCallback(async () => {
    dispatch({ type: HANDLE_FAVOURITE, payload: { id: item._id, type: 'data' } });
    await apis.addFavourite(item._id, 'data', {});
  }, [dispatch, item._id]);

    const handleNavigateToData = useCallback(() => {
      trackInteraction('edit_data');
      // dispatch({ type: SET_ACTIVE_ITEM, payload: item._id });
          dispatch({ type: SET_ACTIVE_ITEM, payload: item._id });
          navigation.navigate('DataBrowser', {
            requestId: item._id,
            title: item.title,
            tags: item.dataTags,
            type: 'invoice',
          });
    }, [dispatch, navigation, item._id, item.dataTitle, item.dataTags, trackInteraction]);

  const handleEditTemplate = useCallback(() => {
    trackInteraction('edit_data');
    dispatch({ type: SET_ACTIVE_ITEM, payload: item._id });
    navigation.navigate('Invoice', {
      item
    });
  }, [dispatch, navigation, item._id, item.dataTitle, item.dataTags, trackInteraction]);

  // Handle navigation for sent invoices
  const handleViewSentInvoice = useCallback(() => {
    trackInteraction('view_invoice');
    navigation.navigate('InvoiceDetail', {
      invoiceId: item._id
    });
  }, [navigation, item._id, trackInteraction]);

  // Determine if this is a sent invoice
  const isSentInvoice = item.dataType === 'sent_invoice' || item.invoice_number;

  const handleNavigateToActions = useCallback(() => {
    trackInteraction('view_actions');
    navigation.navigate('Data', {
      screen: 'ActionHandler',
      params: {
        dataId: item._id, // Use dataId to match route definition
        dataTitle: item.dataTitle 
      } 
    });
  }, [navigation, item._id, item.dataTitle, trackInteraction]);

  const handleNavigateToUtilities = useCallback(() => {
    trackInteraction('view_utilities');
    navigation.navigate('Data', {
      screen: 'Utilities',
      params: {
        id: item._id,
        dataTitle: item.dataTitle 
      } 
    });
  }, [navigation, item._id, item.dataTitle, trackInteraction]);


  const handleNavigateToDataHandler = useCallback(() => {
    trackInteraction('open_data');
    navigation.navigate('Data', {
      screen: 'DataHandler',
      params: {
        id: item._id.toString()
      }
    });
  }, [navigation, item._id, trackInteraction]);

  return (
    <Animatable.View
      animation="fadeInUp"
      duration={600}
      delay={index * 100}
      style={[
        styles.modernCard,
        {
          margin: showCompactMode ? 8 : 12,
          minHeight: showCompactMode ? 280 : 320,
          maxWidth: listHorizontal ? 400 : undefined,
          width: listHorizontal ? 400 : undefined,
        }
      ]}
    >
      {/* Header Section */}
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        padding: showCompactMode ? 12 : 16,
        borderBottomWidth: 1,
        borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
      }}>
        {/* Invoice Icon */}
        <View style={{
          width: showCompactMode ? 36 : 44,
          height: showCompactMode ? 36 : 44,
          borderRadius: showCompactMode ? 18 : 22,
          backgroundColor: theme ? '#3b82f6' : '#1d4ed8',
          alignItems: 'center',
          justifyContent: 'center',
          borderWidth: 2,
          borderColor: theme ? '#e2e8f0' : '#334155',
        }}>
          <MaterialIcons
            name="receipt"
            size={showCompactMode ? 18 : 22}
            color="#ffffff"
          />
        </View>

        {/* Title and Metadata */}
        <View style={{ flex: 1, marginLeft: 12 }}>
          <XStack alignItems="center" gap={8}>
            <H4
              numberOfLines={1}
              color={theme ? "#1e293b" : "#f1f5f9"}
              fontSize={showCompactMode ? 16 : 18}
              fontWeight="600"
              flex={1}
            >
              {isSentInvoice ? (item.invoice_number || parseByDelimiter(item.title)) : parseByDelimiter(item.title)}
            </H4>
            {isSentInvoice && item.status && (
              <View style={{
                backgroundColor: item.status === 'sent' ? '#3B82F6' : 
                               item.status === 'viewed' ? '#F59E0B' : 
                               item.status === 'paid' ? '#10B981' : '#6B7280',
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 8,
              }}>
                <SizableText fontSize={10} color="white" fontWeight="600">
                  {item.status.toUpperCase()}
                </SizableText>
              </View>
            )}

          </XStack>

          <XStack alignItems="center" gap={8} marginTop={4}>
            {isSentInvoice ? (
              <>
                <SizableText
                  fontSize={12}
                  color={theme ? "#64748b" : "#94a3b8"}
                >
                  {item.client?.name || 'Client'}
                </SizableText>
                <View style={{
                  width: 4,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: theme ? "#64748b" : "#94a3b8",
                }} />
                <SizableText
                  fontSize={12}
                  color={theme ? "#64748b" : "#94a3b8"}
                >
                  {item.currency} {item.total?.toFixed(2) || '0.00'}
                </SizableText>
                {item.issue_date && (
                  <>
                    <View style={{
                      width: 4,
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: theme ? "#64748b" : "#94a3b8",
                    }} />
                    <SizableText
                      fontSize={12}
                      color={theme ? "#64748b" : "#94a3b8"}
                    >
                      {moment(item.issue_date).format('MMM DD')}
                    </SizableText>
                  </>
                )}
              </>
            ) : (
              <>
                <SizableText
                  fontSize={12}
                  color={theme ? "#64748b" : "#94a3b8"}
                >
                  {getDaysAgo(item.updatedAt)}
                </SizableText>
                <View style={{
                  width: 4,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: theme ? "#64748b" : "#94a3b8",
                }} />
                <SizableText
                  fontSize={12}
                  color={theme ? "#64748b" : "#94a3b8"}
                >
                  {folderStats?.count || item.count || 0} items
                </SizableText>
              </>
            )}
            {(item.viewCount > 0 || item.interactionCount > 0) && (
              <>
                <View style={{
                  width: 4,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: theme ? "#64748b" : "#94a3b8",
                }} />
                <SizableText
                  fontSize={12}
                  color={theme ? "#64748b" : "#94a3b8"}
                >
                  {item.viewCount || 0}👁 {item.interactionCount || 0}⚡
                </SizableText>
              </>
            )}
          </XStack>
        </View>

        {/* Actions Menu */}
        <XStack alignItems="center" gap={8}>
          <Pressable style={{
            padding: 8,
            borderRadius: 20,
            backgroundColor: theme ? '#f8fafc' : '#1e293b',
          }} onPress={isSentInvoice ? handleViewSentInvoice : handleNavigateToData}>
            <MaterialIcons
              name="edit"
              size={16}
              color={theme ? "#64748b" : "#94a3b8"}
            />
          </Pressable>
          {/* Favorite Button */}
          <Pressable
            onPress={handleFavourite}
            style={{
              padding: 8,
              borderRadius: 20,
              backgroundColor: theme ? '#f8fafc' : '#1e293b',
            }}
          >
            <FontAwesome
              name={isFavourite ? "heart" : "heart-o"}
              size={16}
              color={isFavourite ? "#ef4444" : (theme ? "#64748b" : "#94a3b8")}
            />
          </Pressable>

          {/* Ellipsis Menu */}
          <Popover
            open={isMenuOpen}
            onOpenChange={setIsMenuOpen}
            placement="bottom-end"
          >
            <Popover.Trigger asChild>
              <Pressable
                style={{
                  padding: 8,
                  borderRadius: 20,
                  backgroundColor: theme ? '#f8fafc' : '#1e293b',
                }}
              >
                <MaterialIcons
                  name="more-vert"
                  size={16}
                  color={theme ? "#64748b" : "#94a3b8"}
                />
              </Pressable>
            </Popover.Trigger>

            <Popover.Content
              borderWidth={1}
              borderColor={theme ? "$borderColor" : "$borderColorDark"}
              backgroundColor={theme ? "$background" : "$backgroundDark"}
              padding={0}
              minWidth={180}
              borderRadius={12}
              elevation={5}
            >
              <YStack>
                {/* Document Count */}
                <View style={{
                  padding: 12,
                  alignItems: 'center',
                  borderBottomWidth: 1,
                  borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                }}>
                  <SizableText fontSize={18} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                    {folderStats?.count || item.count || 0}
                  </SizableText>
                  <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                    Documents
                  </SizableText>
                </View>

                {/* Stats Section */}
                <View style={{
                  flexDirection: 'row',
                  borderBottomWidth: 1,
                  borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                }}>
                  <View style={{
                    flex: 1,
                    padding: 12,
                    alignItems: 'center',
                    borderRightWidth: 1,
                    borderRightColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                  }}>
                    <SizableText fontSize={16} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                      {item.viewCount || 0}
                    </SizableText>
                    <SizableText fontSize={10} color={theme ? "#64748b" : "#94a3b8"}>
                      Views
                    </SizableText>
                  </View>
                  <View style={{
                    flex: 1,
                    padding: 12,
                    alignItems: 'center',
                  }}>
                    <SizableText fontSize={16} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                      {item.interactionCount || 0}
                    </SizableText>
                    <SizableText fontSize={10} color={theme ? "#64748b" : "#94a3b8"}>
                      Interactions
                    </SizableText>
                  </View>
                </View>

                {/* Share Action */}
                {Platform.OS !== 'web' && hasPermission() && (
                  <>
                    <Pressable
                      onPress={() => {
                        setIsMenuOpen(false);
                        handleShareState?.(item);
                      }}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        gap: 12,
                      }}
                    >
                      <MaterialIcons
                        name="share"
                        size={18}
                        color={theme ? "#0ea5e9" : "#38bdf8"}
                      />
                      <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                        Share
                      </SizableText>
                    </Pressable>

                    <View style={{
                      height: 1,
                      backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                      marginHorizontal: 12,
                    }} />
                  </>
                )}

                {/* Actions */}
                {hasPermission() && (
                  <XStack gap={20}>
                    <Pressable
                      onPress={() => {
                        setIsMenuOpen(false);
                        handleNavigateToActions();
                      }}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        gap: 12,
                      }}
                    >
                      <MaterialIcons
                        name="settings"
                        size={18}
                        color={theme ? "#059669" : "#10b981"}
                      />
                      <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                        Actions
                      </SizableText>
                    </Pressable>

                    <Pressable
                      onPress={() => {
                        setIsMenuOpen(false);
                        handleNavigateToUtilities();
                      }}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        gap: 12,
                      }}
                    >
                      <MaterialIcons
                        name="settings"
                        size={18}
                        color={theme ? "#059669" : "#10b981"}
                      />
                      <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                        Utilities
                      </SizableText>
                    </Pressable>

                    <View style={{
                      height: 1,
                      backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                      marginHorizontal: 12,
                    }} />
                  </XStack>
                )}

                {/* Settings */}
                {hasPermission() && (
                  <>
                    <Pressable
                      onPress={() => {
                        setIsMenuOpen(false);
                        handleSettingsModal?.(item);
                      }}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        gap: 12,
                      }}
                    >
                      <MaterialIcons
                        name="settings"
                        size={18}
                        color={theme ? "#7c3aed" : "#a78bfa"}
                      />
                      <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                        Settings
                      </SizableText>
                    </Pressable>

                    <View style={{
                      height: 1,
                      backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                      marginHorizontal: 12,
                    }} />
                  </>
                )}

                {/* Generate Link */}
                {hasPermission() && (
                  <>
                    <Pressable
                      onPress={() => {
                        setIsMenuOpen(false);
                        handleGenLink?.(item);
                      }}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        gap: 12,
                      }}
                    >
                      <MaterialIcons
                        name="link"
                        size={18}
                        color={theme ? "#f59e0b" : "#fbbf24"}
                      />
                      <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                        Generate Link
                      </SizableText>
                    </Pressable>

                    <View style={{
                      height: 1,
                      backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                      marginHorizontal: 12,
                    }} />
                  </>
                )}

                {/* Search */}
                <Pressable
                  onPress={() => {
                    setIsMenuOpen(false);
                    handleSearch?.(item);
                  }}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: 12,
                    gap: 12,
                  }}
                >
                  <MaterialIcons
                    name="search"
                    size={18}
                    color={theme ? "#6366f1" : "#818cf8"}
                  />
                  <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                    Search
                  </SizableText>
                </Pressable>

                {/* Delete */}
                {hasPermission() && (
                  <>
                    <View style={{
                      height: 1,
                      backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                      marginHorizontal: 12,
                    }} />

                    <Pressable
                      onPress={() => {
                        setIsMenuOpen(false);
                        handleDeleteModal?.(item);
                      }}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        gap: 12,
                      }}
                    >
                      <MaterialIcons
                        name="delete"
                        size={18}
                        color="#ef4444"
                      />
                      <SizableText fontSize={14} color="#ef4444">
                        Delete
                      </SizableText>
                    </Pressable>
                  </>
                )}
              </YStack>
            </Popover.Content>
          </Popover>
        </XStack>
      </View>

      {/* Content Section */}
      <View style={{ flex: 1, padding: showCompactMode ? 12 : 16 }}>
        {/* Data Type Icons */}
        {item.dataTags && (
          <XStack gap={8} marginBottom={12}>
            {iconMap.map((iconItem) => {
              const hasTag = Array.isArray(item.dataTags)
                ? item.dataTags.includes(iconItem.type)
                : typeof item.dataTags === 'string'
                  ? item.dataTags.includes(iconItem.type)
                  : false;

              if (hasTag) {
                return (
                  <View
                    key={iconItem.type}
                    style={{
                      backgroundColor: theme ? '#e2e8f0' : '#334155',
                      padding: 6,
                      borderRadius: 8,
                    }}
                  >
                    {iconItem.icon}
                  </View>
                );
              }
              return null;
            })}
          </XStack>
        )}

        {/* Stats Row */}
        <XStack justifyContent="space-between" alignItems="center" marginBottom={16}>
          <XStack gap={16}>
            <View style={{ alignItems: 'center' }}>
              <MaterialIcons
                name="folder"
                size={20}
                color={theme ? "#3b82f6" : "#60a5fa"}
              />
              <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                Files
              </SizableText>
            </View>

            <View style={{ alignItems: 'center' }}>
              <MaterialIcons
                name="people"
                size={20}
                color={theme ? "#059669" : "#10b981"}
              />
              <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                {folderStats?.contributorList?.length || 0}
              </SizableText>
            </View>

            <View style={{ alignItems: 'center' }}>
              <MaterialIcons
                name="visibility"
                size={20}
                color={theme ? "#7c3aed" : "#a78bfa"}
              />
              <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                Views
              </SizableText>
            </View>
          </XStack>

          {/* Owner Indicator */}
          {!isOwner() && (
            <View style={{
              backgroundColor: '#10b981',
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 12,
            }}>
              <SizableText fontSize={10} color="white" fontWeight="600">
                Shared
              </SizableText>
            </View>
          )}
        </XStack>

        {/* Tags */}
        {item.dataTags && typeof item.dataTags === 'string' && (
          <XStack gap={6} flexWrap="wrap" marginBottom={16}>
            {item.dataTags.split(',').slice(0, 3).map((tag: string, idx: number) => (
              <View
                key={idx}
                style={{
                  backgroundColor: theme ? '#f1f5f9' : '#334155',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 8,
                }}
              >
                <SizableText fontSize={11} color={theme ? "#475569" : "#cbd5e1"}>
                  {tag.trim()}
                </SizableText>
              </View>
            ))}
          </XStack>
        )}
        {/* Handle array case for dataTags */}
        {item.dataTags && Array.isArray(item.dataTags) && (
          <XStack gap={6} flexWrap="wrap" marginBottom={16}>
            {item.dataTags.slice(0, 3).map((tag: string, idx: number) => (
              <View
                key={idx}
                style={{
                  backgroundColor: theme ? '#f1f5f9' : '#334155',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 8,
                }}
              >
                <SizableText fontSize={11} color={theme ? "#475569" : "#cbd5e1"}>
                  {typeof tag === 'string' ? tag.trim() : String(tag).trim()}
                </SizableText>
              </View>
            ))}
          </XStack>
        )}
      </View>

      {/* Action Buttons */}
      <View style={{
        flexDirection: showCompactMode ? 'column' : 'row',
        padding: showCompactMode ? 12 : 16,
        gap: showCompactMode ? 8 : 12,
        borderTopWidth: 1,
        borderTopColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
      }}>
        {/* Quick Actions */}
        <XStack gap={8} flex={showCompactMode ? undefined : 1}>
          <Pressable
            onPress={() => handleQuickAdd?.(item)}
            disabled={!hasPermission()}
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 8,
              borderRadius: 8,
              backgroundColor: hasPermission()
                ? (theme ? '#f0f9ff' : '#0c4a6e')
                : (theme ? '#f1f5f9' : '#1e293b'),
              opacity: hasPermission() ? 1 : 0.5,
            }}
          >
            <MaterialIcons
              name="send"
              size={16}
              color={hasPermission() ? (theme ? '#0ea5e9' : '#38bdf8') : (theme ? '#94a3b8' : '#64748b')}
            />
            <SizableText
              fontSize={12}
              color={hasPermission() ? (theme ? '#0ea5e9' : '#38bdf8') : (theme ? '#94a3b8' : '#64748b')}
              marginLeft={4}
            >
              Send
            </SizableText>
          </Pressable>

          <Pressable
            onPress={handleNavigateToActions}
            disabled={!hasPermission()}
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 8,
              borderRadius: 8,
              backgroundColor: hasPermission()
                ? (theme ? '#f0fdf4' : '#064e3b')
                : (theme ? '#f1f5f9' : '#1e293b'),
              opacity: hasPermission() ? 1 : 0.5,
            }}
          >
            <MaterialIcons
              name="settings"
              size={16}
              color={hasPermission() ? (theme ? '#059669' : '#10b981') : (theme ? '#94a3b8' : '#64748b')} 
            />
            <SizableText
              fontSize={12}
              color={hasPermission() ? (theme ? '#059669' : '#10b981') : (theme ? '#94a3b8' : '#64748b')}
              marginLeft={4}
            >
              Actions
            </SizableText>
          </Pressable>
        </XStack>

        {/* Primary Action */}
        <Button
          onPress={isSentInvoice ? handleViewSentInvoice : handleNavigateToData}
          customStyle={{
            flex: showCompactMode ? undefined : 2,
            minHeight: 40,
            borderRadius: 8,
            backgroundColor: theme ? '#3b82f6' : '#1d4ed8',
          }}
          buttonText={t('View Filled')}
        />
      </View>
    </Animatable.View>
  );
});
// Export with both names for compatibility
export default memo(DataItem);
export const RenderDataItems = memo(DataItem);